import {ComponentProps, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {MatchListRow} from './MatchListRow';
import {BasicTable, TableSortConfig} from '../../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../../components/BasicTable/createBasicTableColumn';
import {TablePaginator} from '../../../../../components/TablePaginator/TablePaginator';
import {greenColors} from '../../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../../lib/AppConfig';
import {MatchData} from '../../../../../types/shared-recruiting-types';

interface Props {
  matches: MatchData[];
  pagination?: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  hasNext?: boolean;
  getNext?: () => void;
}

type RowProps = ComponentProps<typeof MatchListRow>;

const MATCH_STATUS_SORT_RANK: Record<string, number> = {
  accepted: 0,
  contacted: 1,
  on_hold: 2,
  interview: 3,
  in_interview: 3,
  hired: 4,
  rejected: 5,
};

export const MatchListTable = ({matches, pagination, hasNext = false, getNext}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'matchesPage.tableColumns'});
  const [page, setPage] = useState(1);

  const itemsPerPage = AppConfig.PAGINATION_PER_PAGE;
  const totalItems = pagination?.totalCount ?? matches.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    const requiredItems = safePage * itemsPerPage;
    const reportedTotal = pagination?.totalCount;
    const mayHaveMoreOnServer = reportedTotal === undefined ? Boolean(hasNext) : matches.length < reportedTotal;
    if (matches.length < requiredItems && mayHaveMoreOnServer && hasNext && getNext) {
      getNext();
    }
  }, [safePage, itemsPerPage, matches.length, hasNext, getNext, pagination?.totalCount]);

  const pagedMatches = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return matches.slice(start, start + itemsPerPage);
  }, [matches, safePage, itemsPerPage]);

  const columns = useMemo(
    () => [
      createBasicTableColumn<RowProps>('candidate', t('candidate'), {
        sortable: true,
        getSortValue: (row) => {
          const first = row.applicant?.personalInformation?.firstName ?? '';
          const last = row.applicant?.personalInformation?.lastName ?? '';
          return `${first} ${last}`.trim();
        },
        sx: {width: '15%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('position', t('position'), {
        sortable: true,
        getSortValue: (row) => row.companyJobPosting?.title ?? '',
        sx: {width: '16%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('status', t('status'), {
        sortable: true,
        getSortValue: (row) => MATCH_STATUS_SORT_RANK[row.status] ?? 99,
        sx: {width: '9%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('date', t('date'), {
        sortable: true,
        getSortValue: (row) => row.createdAt ?? null,
        sx: {width: '10%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('total_score', t('total'), {
        sortable: true,
        getSortValue: (row) => row.matchInformationOverview?.overallScore ?? null,
        sx: {width: '11%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('job_score', t('job'), {
        sortable: true,
        getSortValue: (row) => row.matchInformationOverview?.jobMatchScore ?? null,
        align: 'center',
        sx: {width: '7%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('test_score', t('test'), {
        sortable: true,
        getSortValue: (row) => row.matchInformationOverview?.assessmentScore ?? null,
        align: 'center',
        sx: {width: '7%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('culture_score', t('culture'), {
        sortable: true,
        getSortValue: (row) => row.matchInformationOverview?.culturalMatchScore ?? null,
        align: 'center',
        sx: {width: '7%', minWidth: 0},
      }),
      createBasicTableColumn('actions', '', {
        align: 'right',
        sx: {width: '18%', minWidth: 0},
      }),
    ],
    [t]
  );

  const rowData = pagedMatches.map((data) => ({id: data.resourceId, ...data}));

  const initialSortConfig: TableSortConfig = {
    columnId: 'date',
    direction: 'desc',
  };

  return (
    <Box width='100%'>
      <BasicTable<RowProps>
        columns={columns}
        Row={MatchListRow}
        rowData={rowData}
        initialSortConfig={initialSortConfig}
        hideLoadMoreFooter
        fullWidthLayout
        headerTextColor={greenColors[700]}
      />
      <TablePaginator
        page={safePage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        translationKeyPrefix='matchesPage.pagination'
        onPageChange={setPage}
      />
    </Box>
  );
};
